import { APICompsSchema } from "@unit/apis";
import { isDateBefore } from "@unit/utility";
import { signOut } from "firebase/auth";
import { useAtom } from "jotai";
import localforage from "localforage";
import React, { useEffect } from "react";
import { useQueries } from "react-query";

import InitialScreenContainer from "@/components/common/InitialScreenContainer";
import { BackdropProgress } from "@/components/provider/BackdropProgress";
import { useAreaApi } from "@/custom-hooks/apis/use-area-api";
import { useGenreApi } from "@/custom-hooks/apis/use-genre-api";
import { useStaffApi } from "@/custom-hooks/apis/use-staff-api";
import { useAppSnackbar } from "@/custom-hooks/use-app-snackbar";
import { useAuthentication } from "@/custom-hooks/use-authentication";
import { useResetAuth } from "@/custom-hooks/use-reset-auth";
import { STAFF_STORE_LIST_MONTHS_OFFSET } from "@/global-state/app-config";
import { firebaseGetAuth } from "@/global-state/firebase-settings";
import { authUserAtom, idTokenAtom, selectStoreAtom } from "@/global-state/jotai-atom";
import { QueryKeys } from "@/global-state/react-query-keys";

type Props = {
  children: React.ReactNode;
};

const AuthProvider: React.FC<Props> = (props) => {
  const { setAppSnackbar } = useAppSnackbar();
  const { resetAuth } = useResetAuth();
  const { syncIdToken } = useAuthentication();
  const [authUser, setAuthUser] = useAtom(authUserAtom);
  const [idToken] = useAtom(idTokenAtom);

  const [, setSelectStoreAtom] = useAtom(selectStoreAtom);

  const { getAreaList } = useAreaApi();
  const { getGenreList } = useGenreApi();
  const { getStaffProfile } = useStaffApi();

  const handleWindowFocusChange = async () => {
    if (!document.hidden) {
      await syncIdToken();
    }
  };
  useEffect(() => {
    document.addEventListener("visibilitychange", handleWindowFocusChange);
    return () => {
      document.removeEventListener("visibilitychange", handleWindowFocusChange);
    };
  }, []);

  useEffect(() => {
    (async () => {
      const accessToken = await syncIdToken();
      if (!accessToken) {
        return await resetAuth();
      }
    })();
  }, [idToken]);

  useEffect(() => {
    if (!authUser?.id) {
      return;
    }
    const resetInterval = setInterval(() => syncIdToken(), 10 * 60 * 1000); // 10分間
    return () => clearInterval(resetInterval);
  }, [authUser?.id]);

  // GlobalStateとして扱うデータをAPIから取得
  const initApiResults = useQueries([
    {
      queryKey: QueryKeys.getAreaList,
      queryFn: () => getAreaList(),
    },
    {
      queryKey: QueryKeys.getGenreList,
      queryFn: () => getGenreList(),
    },
    {
      queryKey: [QueryKeys.getStaffProfile],
      queryFn: () => getStaffProfile(),
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onSuccess: async (res: any) => {
        const staff = res.object as APICompsSchema["StaffObject"];
        await setAuthUser(staff);
        const selectableStores = staff.stores.filter(
          (s) => !isDateBefore(s.contractEndDate, undefined, STAFF_STORE_LIST_MONTHS_OFFSET),
        );
        const storeId = await localforage.getItem("selected_store_id");
        const targetStore = selectableStores.find((store) => store.id === storeId) || selectableStores[0];
        if (!targetStore) {
          setAppSnackbar("どのストアにも所属していないユーザーです", { error: true });
          await resetAuth();
          return;
        }
        setSelectStoreAtom(targetStore);
      },
      onError: async (e: any) => {
        const status = e?.response?.status;
        const errorObj = e?.response?.data;
        console.error(errorObj?.message || errorObj?.devMessage);
        if (status === 401) {
          await signOut(firebaseGetAuth);
        }
        await resetAuth();
      },
      enabled: !!idToken,
    },
  ]);

  const loading = initApiResults[0].isLoading || initApiResults[1].isLoading || initApiResults[2].isLoading;

  if (loading && !authUser?.id) {
    return (
      <BackdropProgress open>
        <InitialScreenContainer />
      </BackdropProgress>
    );
  }

  return <>{props.children}</>;
};

export default AuthProvider;
